import { aecomLogo24 } from '@aecom-ai-engineering/design-styles/logos/aecom';
import { type FC } from 'react';
import { Link } from 'react-router';

import { BrandBackdrop } from '@/components/brand-backdrop';
import { Route } from '@/routes';

const LOGO_POSITION = 'absolute left-8 top-8 flex items-center';

/**
 * The design system's own lockup at the 24px step, which ships 105x24 —
 * the frame's box to the pixel, so the SVG's root width and height are
 * the sizing and there is no `h-6 w-[105px]` here to drift from them.
 * That is also why it is this export and not @/assets/aecom-logo-black:
 * the local file is the legacy PageHeader's, at no particular size.
 *
 * An SVG source string, so dangerouslySetInnerHTML is the only way in —
 * the same route agent-logo-marquee.tsx takes, and the one the design
 * system's own SideNavBar takes for this very asset. No colour wiring:
 * the paths carry #0C0A09 rather than currentColor.
 *
 * Named rather than decorative, because this screen carries no app chrome
 * and the mark is the only thing on it that says whose product it is.
 * `role="img"` with a label is what makes the inline SVG announce as one
 * thing instead of as its paths.
 */
const AecomMark: FC = () => (
  <span
    aria-label="AECOM"
    className="flex items-center"
    dangerouslySetInnerHTML={{ __html: aecomLogo24 }}
    role="img"
  />
);

interface BrandRailProps {
  linksHome: boolean;
}

/**
 * The 320px brand column down the left edge of both create-project frames:
 * the AECOM mark inset 32 from the top left, over the dithered brand field.
 * Figma 11914:189834 and 11914:190243.
 *
 * `shape="halo"` — the dome on the bottom edge — rather than the frames' own
 * `Screenshot 2026-07-20 at 10.12.10`, which is a capture of this renderer
 * pasted onto the canvas: the grey strip along its bottom is the screenshot's
 * own edge, and the second frame crops it away. So the component is the thing
 * the capture was taken of, not a substitute for it.
 *
 * ## Why it is the height of the viewport, not of the page
 *
 * `h-full` against the page's `h-screen` row, so the column is exactly the
 * viewport and never grows with the form beside it. `<main>` is the scroll
 * container, so nothing here has to stick: the rail cannot move, because the
 * page it sits on does not scroll. See create-project-page.tsx.
 *
 * `self-stretch` was the obvious thing and the wrong one. It made the rail as
 * tall as the form — the taller of the two by some way once the type groups
 * are open — and the dither then sat in the bottom 60% of a 1200px column,
 * most of it below the fold. That is what read as the rail being cropped.
 *
 * `relative` is the surface the canvas positions against and `overflow-hidden`
 * keeps it inside the column.
 *
 * ## Why the canvas is the lower three fifths of the column
 *
 * Backdrop.css positions it `inset: 0` at `height: 100%`, so left alone it
 * covers the rail top to bottom and the dither climbs past the AECOM mark.
 * `top-2/5 h-3/5` moves it to the bottom 60%, flush with the bottom edge: the
 * box is over-constrained once `inset: 0` has set `bottom`, and CSS resolves
 * that by ignoring `bottom` when `top` and `height` are both given.
 *
 * **Percentages, never a pixel height.** Both values resolve against the rail,
 * which is the viewport's height — and that is no more fixed than the page
 * was. A stated `h-[460px]` would be 60% of one laptop and a band floating
 * above the bottom edge on the next.
 *
 * The height still has to be *stated* as a length, though. `top-2/5 bottom-0
 * h-auto` looks like the same box and is not: a canvas is a replaced element,
 * so `height: auto` takes the bitmap's intrinsic height and `bottom` is
 * dropped as over-constrained. The renderer then sizes the bitmap from the box
 * it measures, the two agree on whatever they started at, and the dither
 * renders as a short strip that never reaches the bottom of the rail.
 *
 * Utilities rather than a rule of our own: Tailwind's utilities layer sorts
 * after the design system's components layer, so these outrank Backdrop.css on
 * layer order without out-specifying its `[data-ds-brand-backdrop]` selector —
 * which is what scripts/check-css-layers.mjs is there to refuse.
 *
 * ## Why the mark links home only some of the time
 *
 * `linksHome` makes the mark a link to the project list, the same destination
 * as the form's Back button. The page turns it off while the project is being
 * created, as it hides Back then: the redirect to the new project would follow
 * the user to the list and pull them off it again.
 *
 * Hidden below `md`. The frames draw one width, 1440, and on a phone a 320px
 * column would leave the form 55px. Nothing is lost with it gone: it carries
 * the logo and the brand field, and no copy.
 */
export const BrandRail: FC<BrandRailProps> = ({ linksHome }) => (
  <aside className="relative hidden h-full w-80 shrink-0 overflow-hidden bg-[var(--ds-color-bg-brand-light)] md:block">
    <BrandBackdrop className="top-2/5 h-3/5" shape="halo" />
    {linksHome ? (
      <Link className={LOGO_POSITION} to={`/${Route.PROJECTS}`}>
        <AecomMark />
      </Link>
    ) : (
      <div className={LOGO_POSITION}>
        <AecomMark />
      </div>
    )}
  </aside>
);
